import React, { useState, useEffect, useRef } from "react";
import {
  Grid,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import request from "../../../utilities/requests";
import useProfilePhoto from "../../../utilities/useProfilePhoto";
import ProfilePhotoBanner from "../../../components/ProfilePhotoBanner";
import { pillFieldSx } from "../../../utilities/pillFieldSx";

const ProfileHeader = ({ profile, refresh }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const inputFile = useRef(null);

  const [open, setOpen] = useState(false);
  const [firstName, setFirstName] = useState(profile?.firstName || "");
  const [lastName, setLastName] = useState(profile?.lastName || "");
  const [savingName, setSavingName] = useState(false);

  const {
    loading,
    previewUrl,
    syncToProfileImage,
    upload,
    defaultFocal,
  } = useProfilePhoto({ onUploaded: refresh, onFailed: refresh });

  const [focalX, setFocalX] = useState(profile?.photoFocalX ?? 50);
  const [focalY, setFocalY] = useState(profile?.photoFocalY ?? 30);

  useEffect(() => {
    if (profile) {
      setFirstName(profile?.firstName || "");
      setLastName(profile?.lastName || "");

      syncToProfileImage(profile?.imageUrl);

      const fx = profile?.photoFocalX ?? 50;
      const fy = profile?.photoFocalY ?? 30;
      setFocalX(fx);
      setFocalY(fy);
    }
  }, [profile, syncToProfileImage]);

  const handleUpload = async (event) => {
    const file = event.target.files[0];
    event.target.value = "";
    if (!file) return;
    // Reset focal point to default after new upload
    setFocalX(defaultFocal.x);
    setFocalY(defaultFocal.y);
    await upload(file);
  };

  const handleSaveName = async () => {
    if (!canSaveName || savingName) return;
    setSavingName(true);
    const result = await request("profile/update", "post", {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
    });
    setSavingName(false);
    if (result?.success) {
      refresh();
      setOpen(false);
    }
  };

  const handleCancelName = () => {
    if (!canSaveName || savingName) return;
    setFirstName(profile?.firstName || "");
    setLastName(profile?.lastName || "");
    setOpen(false);
  };

  const photoUrl = previewUrl || profile?.imageUrl;
  const displayName = [profile?.firstName, profile?.lastName]
    .map((part) => (part || "").trim())
    .filter(Boolean)
    .join(" ");
  const hasName = Boolean(displayName);
  const hasFullName = Boolean(
    profile?.firstName?.trim() && profile?.lastName?.trim()
  );
  const canSaveName = Boolean(firstName?.trim() && lastName?.trim());

  return (
    <div style={{ width: "100%" }}>
      <ProfilePhotoBanner
        photoUrl={photoUrl}
        loading={loading}
        focalX={focalX}
        focalY={focalY}
        onSaved={(x, y) => {
          setFocalX(x);
          setFocalY(y);
        }}
        onPickPhoto={() => inputFile.current.click()}
        cameraBadgeProps={{ "data-tour": "profile-photo" }}
      />

      {/* Below-banner content */}
      <Grid
        container
        direction="column"
        spacing={2}
        justifyContent="center"
        alignItems="center"
        style={{ marginTop: 16 }}
      >
        {/* Name - a tappable chip that opens the edit dialog. Empty state shows
            a coral-bordered "Add your name" prompt instead of a red error. */}
        <Grid item xs={12} style={{ display: "flex", justifyContent: "center" }}>
          <div
            data-tour="profile-name"
            onClick={() => setOpen(true)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              border: `1.5px solid ${hasFullName ? "#efe6e0" : theme.palette.primary.main}`,
              background: "#fbf6f2",
              borderRadius: 12,
              padding: "9px 16px",
              cursor: "pointer",
            }}
          >
            <Typography
              variant="h5"
              style={{
                color: hasName ? undefined : "#a1968f",
                fontWeight: hasName ? undefined : 500,
              }}
            >
              {hasName ? displayName : t("profile.addname.label")}
            </Typography>
            <EditIcon style={{ color: theme.palette.primary.main, width: 20 }} />
          </div>
        </Grid>
      </Grid>

      <input
        type="file"
        accept="image/*"
        id="file"
        ref={inputFile}
        style={{ display: "none" }}
        onChange={handleUpload}
      />

      <Dialog
        open={open}
        onClose={handleCancelName}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: "24px" } }}
      >
        <DialogTitle>{t("profile.editnamedialog.title")}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="dense"
            sx={pillFieldSx}
            label={t("profile.editfirstname.label")}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          <TextField
            fullWidth
            margin="dense"
            sx={pillFieldSx}
            label={t("profile.editlastname.label")}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
          {!canSaveName && (
            <Typography
              sx={{ fontSize: 13, color: "text.secondary", mt: 1, px: "10px" }}
            >
              {t("profile.editname.helper")}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            className="tnfm-pill-btn"
            style={{ minHeight: 32, paddingLeft: 16, paddingRight: 16 }}
            onClick={handleCancelName}
            disabled={!canSaveName || savingName}
          >
            {t("dialog.cancel.button")}
          </Button>
          <Button
            className="tnfm-pill-btn"
            style={{ minHeight: 32, paddingLeft: 24, paddingRight: 24 }}
            variant="contained"
            onClick={handleSaveName}
            disabled={!canSaveName || savingName}
          >
            {savingName ? <CircularProgress size={20} /> : t("dialog.save.button")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ProfileHeader;
